import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Plus, Copy, Trash2, Lock, ChevronRight } from 'lucide-react'
import { getPdfTemplates, clonePdfTemplate, deletePdfTemplate, createPdfTemplate } from '../api/storage'
import { Modal } from '../components/Modal'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { EmptyState } from '../components/EmptyState'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import { Select, SelectItem } from '../components/ui/select'
import { useDateFmt } from '../hooks/useDateFmt'
import type { PdfTemplate, PdfTemplateType } from '../types'

const TYPE_LABELS: Record<PdfTemplateType, string> = {
  invoice:        'Invoice',
  contract:       'Contract',
  letter:         'Letter',
  purchase_order: 'Purchase Order',
  shipping_label: 'Shipping Label',
  custom:         'Custom',
}

const TYPE_COLORS: Record<PdfTemplateType, string> = {
  invoice:        'bg-blue-50 text-blue-600 border-blue-100',
  contract:       'bg-neutral-50 text-neutral-600 border-neutral-200',
  letter:         'bg-slate-50 text-slate-600 border-slate-200',
  purchase_order: 'bg-emerald-50 text-emerald-600 border-emerald-100',
  shipping_label: 'bg-amber-50 text-amber-600 border-amber-100',
  custom:         'bg-purple-50 text-purple-600 border-purple-100',
}

const TEMPLATE_TYPES: PdfTemplateType[] = ['invoice', 'contract', 'letter', 'purchase_order', 'shipping_label', 'custom']

export function PdfTemplates() {
  const fmt      = useDateFmt()
  const navigate = useNavigate()
  const qc       = useQueryClient()

  const { data: templates = [], isLoading } = useQuery({
    queryKey: ['pdf-templates'],
    queryFn:  () => getPdfTemplates().then(r => r.data),
  })

  // Clone
  const cloneMut = useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) => clonePdfTemplate(id, name),
    onSuccess:  (res) => {
      qc.invalidateQueries({ queryKey: ['pdf-templates'] })
      navigate(`/pdf-templates/${res.data.id}`)
    },
  })

  // Delete
  const [deleteTarget, setDeleteTarget] = useState<PdfTemplate | null>(null)
  const delMut = useMutation({
    mutationFn: (id: string) => deletePdfTemplate(id),
    onSuccess:  () => { qc.invalidateQueries({ queryKey: ['pdf-templates'] }); setDeleteTarget(null) },
  })

  // Create blank
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', description: '', templateType: 'custom' as PdfTemplateType })
  const createMut = useMutation({
    mutationFn: () => createPdfTemplate({
      name: createForm.name,
      description: createForm.description || undefined,
      templateType: createForm.templateType,
      config: blankConfig(),
    }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['pdf-templates'] })
      setShowCreate(false)
      navigate(`/pdf-templates/${res.data.id}`)
    },
  })

  const system = (templates as PdfTemplate[]).filter(t => t.isSystem)
  const mine   = (templates as PdfTemplate[]).filter(t => !t.isSystem)

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">PDF Templates</h1>
          <p className="text-sm text-ink-dim mt-0.5">Create and manage document templates for PDF generation</p>
        </div>
        <Button icon={Plus} onClick={() => setShowCreate(true)}>New template</Button>
      </div>

      {/* System templates */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <Lock size={12} className="text-ink-dim" />
          <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest">System templates</h2>
          <span className="text-theme-xs text-ink-dim">— read-only, clone to customize</span>
        </div>
        {isLoading ? (
          <div className="py-6 text-center text-sm text-ink-dim">Loading…</div>
        ) : system.length === 0 ? (
          <p className="text-sm text-ink-dim italic">No system templates found.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {system.map(tpl => (
              <TemplateCard
                key={tpl.id} tpl={tpl} fmt={fmt}
                onClone={() => cloneMut.mutate({ id: tpl.id })}
                cloningId={cloneMut.isPending ? cloneMut.variables?.id : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* My templates */}
      <div>
        <h2 className="text-theme-xs font-semibold text-ink-dim uppercase tracking-widest mb-3">My templates</h2>
        {!isLoading && mine.length === 0 ? (
          <EmptyState icon={FileText} title="No custom templates" description="Clone a system template or create a blank one to get started" />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {mine.map(tpl => (
              <TemplateCard
                key={tpl.id} tpl={tpl} fmt={fmt}
                onEdit={() => navigate(`/pdf-templates/${tpl.id}`)}
                onClone={() => cloneMut.mutate({ id: tpl.id })}
                onDelete={() => setDeleteTarget(tpl)}
                cloningId={cloneMut.isPending ? cloneMut.variables?.id : undefined}
              />
            ))}
          </div>
        )}
      </div>

      {/* Create modal */}
      {showCreate && (
        <Modal title="New template" onClose={() => setShowCreate(false)} size="sm">
          <div className="space-y-4">
            <Field label="Name *">
              <input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. My Invoice" className="field" autoFocus />
            </Field>
            <Field label="Description">
              <input value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Optional" className="field" />
            </Field>
            <Field label="Type">
              <Select value={createForm.templateType} onValueChange={v => setCreateForm(f => ({ ...f, templateType: v as PdfTemplateType }))}>
                {TEMPLATE_TYPES.map(t => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
              </Select>
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => createMut.mutate()} disabled={!createForm.name} loading={createMut.isPending}>
                Create
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Delete confirm */}
      {deleteTarget && (
        <ConfirmDialog
          title="Delete template"
          message={`Delete template "${deleteTarget.name}"? This cannot be undone.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => delMut.mutate(deleteTarget.id)}
          loading={delMut.isPending}
        />
      )}
    </div>
  )
}

function TemplateCard({ tpl, fmt, onEdit, onClone, onDelete, cloningId }: {
  tpl: PdfTemplate
  fmt: (d: any) => string
  onEdit?: () => void
  onClone?: () => void
  onDelete?: () => void
  cloningId?: string
}) {
  const cloning = cloningId === tpl.id
  return (
    <div className="border border-paper-border rounded-lg p-4 hover:border-ink-dim/30 transition-colors">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <FileText size={14} className="text-ink-dim shrink-0" />
          <span className="font-medium text-ink text-sm truncate">{tpl.name}</span>
        </div>
        {tpl.isSystem && <Lock size={11} className="text-ink-dim shrink-0 mt-0.5" />}
      </div>
      {tpl.description && <p className="text-xs text-ink-dim mb-3 leading-relaxed">{tpl.description}</p>}
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-[10px] font-medium px-1.5 py-px rounded border ${TYPE_COLORS[tpl.templateType]}`}>
          {TYPE_LABELS[tpl.templateType]}
        </span>
        {tpl.clonedFrom && <span className="text-[10px] text-ink-dim">cloned</span>}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-theme-xs text-ink-dim">{fmt(tpl.createdAt)}</span>
        <div className="flex items-center gap-1">
          {onClone && (
            <button onClick={onClone} disabled={cloning} title="Clone"
              className="p-1.5 text-ink-dim hover:text-ink transition-colors">
              <Copy size={13} />
            </button>
          )}
          {onEdit && (
            <button onClick={onEdit} title="Edit"
              className="p-1.5 text-ink-dim hover:text-ink transition-colors">
              <ChevronRight size={13} />
            </button>
          )}
          {onDelete && (
            <button onClick={onDelete} title="Delete"
              className="p-1.5 text-ink-dim hover:text-red-500 transition-colors">
              <Trash2 size={13} />
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

function blankConfig() {
  return {
    primaryColor:   '#1e40af',
    secondaryColor: '#eff6ff',
    fontFamily:     'Arial, Helvetica, sans-serif',
    header: {
      enabled: true,
      businessName: 'Your Company Name',
      businessAddress: '',
      businessContact: '',
      titleText: 'DOCUMENT',
      fields: [],
      showRecipient: false,
      recipientLabel: 'To',
      recipientVar: 'recipient',
    },
    sections: [],
    footer: { enabled: true, text: '', showPageNumbers: true },
    pageSize: 'A4' as const,
    orientation: 'portrait' as const,
    margins: { top: 15, right: 15, bottom: 15, left: 15 },
  }
}
